import { store } from '../utils/storage';
import { syncAttemptsWriteTimeoutMs, withTimeout } from './with-timeout.helpers';

// How many sync passes an action may fail identically before it stops blocking the ones behind
// it. Every transient cause — server busy, lease held, a timeout, another scorer signed in —
// clears well inside this; only a genuinely stuck action reaches it.
export const MAX_SYNC_PASSES = 5;

// Counts live beside the buffer rather than on the action: an action is serialized onto the wire,
// and a bookkeeping field there would ride along with it.
export const SYNC_ATTEMPTS_LOCAL_REF = 'max_SyncAttempts';

type AttemptCounts = Record<string, number>;

class SyncAttemptsWriteTimeoutError extends Error {
  readonly reason = 'write-timeout';

  constructor() {
    super('Sync attempt counter write timed out');
    this.name = 'SyncAttemptsWriteTimeoutError';
  }
}

// Serialized read-modify-writes, so a pass that bumps counts never loses a concurrent clear.
let attemptQueue: Promise<unknown> = Promise.resolve();
const withAttemptLock = <T>(write: () => Promise<T>): Promise<T> => {
  const timeoutMs = syncAttemptsWriteTimeoutMs();
  const guardedWrite = () => withTimeout(write(), timeoutMs, () => {
    console.error(`sync-attempt counter write: timed out after ${timeoutMs}ms`);
    return new SyncAttemptsWriteTimeoutError();
  });
  const run = attemptQueue.then(guardedWrite, guardedWrite);
  attemptQueue = run.then(() => undefined, () => undefined);
  return run;
};

export const readSyncAttempts = async (): Promise<AttemptCounts> => {
  try {
    return JSON.parse((await store.get(SYNC_ATTEMPTS_LOCAL_REF)) || '{}') as AttemptCounts;
  } catch (err) {
    // A corrupt or unreadable counter must never stop a sync — it only decides when to stop blocking.
    console.warn('readSyncAttempts: could not read sync attempt counts', err);
    return {};
  }
};

const writeSyncAttempts = (counts: AttemptCounts) =>
  store.set(SYNC_ATTEMPTS_LOCAL_REF, JSON.stringify(counts));

// One pass's outcome: the keys that failed again are bumped, the ones that landed are forgotten.
// Returns the counts as of this pass, so the caller decides without a second read.
export const recordSyncPass = (
  { unackedKeys, ackedKeys = [] }: { unackedKeys: string[]; ackedKeys?: string[] },
): Promise<AttemptCounts> => withAttemptLock(async () => {
  const counts = await readSyncAttempts();
  ackedKeys.forEach((key) => { delete counts[key]; });
  unackedKeys.forEach((key) => { counts[key] = (counts[key] || 0) + 1; });
  await writeSyncAttempts(counts);
  return counts;
});

// For callers that treat the counts as bookkeeping: a failed or timed-out write is logged and yields
// null, so it never fails the sync that called it.
export const tryRecordSyncPass = async (
  pass: Parameters<typeof recordSyncPass>[0],
  context: string,
): Promise<AttemptCounts | null> => {
  try {
    return await recordSyncPass(pass);
  } catch (err) {
    console.warn(`recordSyncPass (${context}): attempt counts not saved; continuing without them`, err);
    return null;
  }
};

// Keys that have now failed MAX_SYNC_PASSES times or more, given this pass's counts.
export const wedgedKeys = (keys: string[], counts: AttemptCounts): string[] =>
  keys.filter((key) => (counts[key] || 0) >= MAX_SYNC_PASSES);

// Drops counts for keys no longer buffered, so the map can't grow without bound.
export const pruneSyncAttempts = (liveKeys: string[]): Promise<void> => withAttemptLock(async () => {
  const counts = await readSyncAttempts();
  const live = new Set(liveKeys);
  const next: AttemptCounts = {};
  Object.keys(counts).forEach((key) => { if (live.has(key)) next[key] = counts[key]; });
  await writeSyncAttempts(next);
});
